import MuiButton from '@mui/material/Button';
import type { ButtonProps } from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { forwardRef, type InputHTMLAttributes } from 'react';

/**
 * App Button: MUI text / contained / outlined / tonal (Material 3 shapes come from the theme).
 * Covers what the app uses: href, disabled, icons, sizes, colors, loading.
 */
export const Button = forwardRef(function Button({ sx, disableElevation = true, ...props }: ButtonProps, ref) {
  return (
    <MuiButton
      ref={ref}
      disableElevation={disableElevation}
      {...props}
      sx={sx}
    />
  );
}) as typeof MuiButton;

const VisuallyHiddenInput = styled('input')({
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export type FileButtonProps = Omit<ButtonProps, 'component' | 'ref'> & {
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
};

/** Upload button with a visually hidden file input (MUI InputFileUpload). */
export function FileButton({ children, inputProps, sx, disableElevation = true, ...props }: FileButtonProps) {
  return (
    <MuiButton
      component="label"
      role={undefined}
      tabIndex={-1}
      disableElevation={disableElevation}
      {...(props as ButtonProps)}
      sx={sx}
    >
      {children}
      <VisuallyHiddenInput type="file" {...inputProps} />
    </MuiButton>
  );
}

export type { ButtonProps };
